/**
 * International welcome country popup.
 * Fetches Shopify browsing_context_suggestions.json; shows popup when detected country
 * differs from current market. "Shop in [country]" submits and redirects; X, backdrop,
 * or Escape just closes. "Change shipping country" opens the footer localization drawer.
 */

const SESSION_STORAGE_KEY = 'welcome_country_popup_shown';

/** @type {HTMLElement | null} Element that had focus before the popup opened; restored on close. */
let previousFocus = null;

const SWIPE_THRESHOLD = 60;
let swipeStartY = 0;
let swipeCurrentY = 0;
/** @type {((e: TouchEvent) => void) | null} */
let swipeTouchStartHandler = null;
/** @type {((e: TouchEvent) => void) | null} */
let swipeTouchMoveHandler = null;
/** @type {((e: TouchEvent) => void) | null} */
let swipeTouchEndHandler = null;

/** @type {((e: TouchEvent) => void) | null} iOS scroll-lock handler */
let scrollLockTouchHandler = null;

/** Fallback country → { symbol, code } when the suggested country isn’t in the theme’s map. */
/** @type {Record<string, { symbol: string; code: string }>} */
const COUNTRY_CURRENCY_FALLBACK = {
  CA: { symbol: '$', code: 'CAD' },
  US: { symbol: '$', code: 'USD' },
  GB: { symbol: '£', code: 'GBP' },
  AU: { symbol: '$', code: 'AUD' },
  EU: { symbol: '€', code: 'EUR' },
};

/**
 * Submit the localization form to redirect to the given country (and optional language).
 * @param {string} countryCode
 * @param {string} [languageCode]
 * @param {string} [returnTo]
 */
function submitLocalizationForm(countryCode, languageCode, returnTo) {
  const id = `localization-${crypto.randomUUID()}`;
  document.body.insertAdjacentHTML(
    'beforeend',
    `<form id="${id}" action="/localization" method="POST" hidden>
      <input name="_method" value="PUT">
      <input name="country_code" value="${countryCode}">
      ${languageCode ? `<input name="language_code" value="${languageCode}">` : ''}
      ${returnTo ? `<input name="return_to" value="${returnTo}">` : ''}
    </form>`,
  );
  /** @type {HTMLFormElement} */ (document.getElementById(id))?.submit();
}

/** Open the localization drawer (country selector). */
function openFooterLocalizationDrawer() {
  const drawer = document.getElementById('localization-drawer');
  const drawerWithShowDialog =
    drawer && typeof drawer === 'object' && 'showDialog' in drawer
      ? /** @type {HTMLElement & { showDialog: () => void }} */ (drawer)
      : null;
  if (drawerWithShowDialog) {
    drawerWithShowDialog.showDialog();
  }
}

/**
 * Replace a placeholder token in an element's textContent.
 * @param {Element | null} el
 * @param {string} token
 * @param {string} value
 */
function replacePlaceholder(el, token, value) {
  if (el) el.textContent = el.textContent.replace(token, value);
}

/**
 * @param {HTMLElement} root
 * @param {{ handle: string, name: string }} country
 */
function showPopup(root, country) {
  const countryName = country.name || country.handle;
  const countryKey = String(country.handle || '')
    .trim()
    .toUpperCase();
  const normalizedCountryName = String(country.name || '')
    .trim()
    .toUpperCase();
  const isCanada =
    countryKey === 'CA' || countryKey === 'CANADA' || normalizedCountryName.includes('CANADA');
  const isUnitedKingdom =
    countryKey === 'GB' ||
    countryKey === 'UK' ||
    normalizedCountryName.includes('UNITED KINGDOM') ||
    normalizedCountryName === 'UK';
  const headingCountryName = isUnitedKingdom ? 'the United Kingdom' : countryName;

  let currencySymbol = '$';
  let currencyCode = 'USD';
  try {
    const scriptEl = document.getElementById('welcome-country-currencies');
    const map = scriptEl ? JSON.parse(scriptEl.textContent || '{}') : {};
    const entry =
      map[countryKey] ??
      map[countryKey.toLowerCase()] ??
      map[country.handle || ''] ??
      COUNTRY_CURRENCY_FALLBACK[countryKey];
    if (entry) {
      if (typeof entry === 'string') {
        currencySymbol = entry;
      } else {
        currencySymbol = entry.symbol ?? currencySymbol;
        currencyCode = entry.code ?? currencyCode;
      }
    }
  } catch {
    // malformed JSON — fall back to default
  }

  let thresholdAmount = '150';
  try {
    const thresholdsEl = document.getElementById('welcome-country-thresholds');
    const thresholdsMap = thresholdsEl ? JSON.parse(thresholdsEl.textContent || '{}') : {};
    const val = thresholdsMap[countryKey] ?? thresholdsMap[countryKey.toLowerCase()];
    if (val != null) thresholdAmount = String(val);
  } catch {
    // malformed JSON — use fallback
  }
  const threshold = `${currencySymbol}${thresholdAmount}`;

  replacePlaceholder(root.querySelector('[data-welcome-popup-heading]'), '[country]', headingCountryName);
  replacePlaceholder(
    root.querySelector('[data-welcome-popup-list-currency]'),
    '[currency]',
    currencyCode,
  );
  replacePlaceholder(
    root.querySelector('[data-welcome-popup-list-shipping]'),
    '[threshold]',
    threshold,
  );
  replacePlaceholder(root.querySelector('[data-welcome-popup-shop-in]'), '[country]', countryName);

  const dutiesEl = root.querySelector('[data-welcome-popup-list-duties]');
  if (dutiesEl instanceof HTMLElement) {
    dutiesEl.hidden = isCanada;
    dutiesEl.style.display = isCanada ? 'none' : '';
  }

  try {
    const ddpConfigEl = document.getElementById('welcome-country-ddp-config');
    if (ddpConfigEl) {
      const ddpConfig = JSON.parse(ddpConfigEl.textContent || '{}');
      const ddpCountries = Array.isArray(ddpConfig.countries)
        ? ddpConfig.countries.map((/** @type {unknown} */ c) => String(c).trim().toUpperCase())
        : [];
      const isDdp = ddpCountries.includes(countryKey);
      if (dutiesEl instanceof HTMLElement && !isCanada) {
        const ukDutiesText =
          typeof ddpConfig.duty_free_text === 'string' && ddpConfig.duty_free_text.trim() !== ''
            ? ddpConfig.duty_free_text
            : ddpConfig.ddp_text;

        dutiesEl.textContent = isUnitedKingdom
          ? ukDutiesText
          : isDdp
            ? ddpConfig.ddp_text
            : ddpConfig.non_ddp_text;
      }
    }
  } catch {
    // malformed DDP config — keep default text
  }

  // Optional fourth bullet, configured per country in theme settings. Absent → stays hidden.
  const extraBulletEl = root.querySelector('[data-welcome-popup-list-extra]');
  if (extraBulletEl instanceof HTMLElement) {
    let extraBulletText = '';
    try {
      const extraBulletsEl = document.getElementById('welcome-country-extra-bullets');
      const extraBulletsMap = extraBulletsEl ? JSON.parse(extraBulletsEl.textContent || '{}') : {};
      const val = extraBulletsMap[countryKey] ?? extraBulletsMap[countryKey.toLowerCase()];
      if (typeof val === 'string') extraBulletText = val.trim();
    } catch {
      // malformed JSON — no extra bullet
    }
    const hasExtraBullet = extraBulletText !== '';
    extraBulletEl.textContent = extraBulletText;
    extraBulletEl.hidden = !hasExtraBullet;
    extraBulletEl.style.display = hasExtraBullet ? '' : 'none';
  }

  root.removeAttribute('hidden');
  root.setAttribute('aria-hidden', 'false');
  root.classList.add('is-open');

  // Lock background scroll (overflow:hidden + iOS touchmove prevention)
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  if (!scrollLockTouchHandler) {
    scrollLockTouchHandler = (e) => {
      if (!(e.target instanceof Element)) {
        e.preventDefault();
        return;
      }
      /** @type {Element | null} */
      let el = e.target;
      const dialogEl = root.querySelector('[role="dialog"]');
      while (el && el !== dialogEl && el !== root) {
        if (
          el.scrollHeight > el.clientHeight &&
          getComputedStyle(el).overflowY !== 'hidden' &&
          getComputedStyle(el).overflowY !== 'visible'
        ) {
          return;
        }
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', scrollLockTouchHandler, { passive: false });
  }

  const dialog = root.querySelector('[role="dialog"]');
  if (dialog instanceof HTMLElement) {
    previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);
    dialog.setAttribute('tabindex', '-1');

    const applyFocus = () => {
      if (root.classList.contains('is-open')) {
        dialog.focus();
      }
    };

    root.addEventListener(
      'transitionend',
      (e) => {
        if (e.target !== root || e.propertyName !== 'opacity') return;
        applyFocus();
      },
      { once: true },
    );

    const durationMs = parseFloat(getComputedStyle(root).transitionDuration || '0.125') * 1000;
    setTimeout(applyFocus, Math.max(150, durationMs + 50));

    swipeTouchStartHandler = (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      swipeStartY = touch.clientY;
      swipeCurrentY = swipeStartY;
    };
    swipeTouchMoveHandler = (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      const deltaY = touch.clientY - swipeStartY;
      if (deltaY <= 0) return;
      e.preventDefault();
      swipeCurrentY = touch.clientY;
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dialog.style.transform = `translateY(${deltaY}px)`;
      }
    };
    swipeTouchEndHandler = () => {
      const deltaY = swipeCurrentY - swipeStartY;
      if (deltaY >= SWIPE_THRESHOLD) {
        dialog.style.transform = '';
        hidePopup(root);
      } else {
        dialog.style.transition = 'transform 0.2s ease';
        dialog.style.transform = '';
        dialog.addEventListener(
          'transitionend',
          () => {
            dialog.style.transition = '';
          },
          { once: true },
        );
      }
    };

    dialog.addEventListener('touchstart', swipeTouchStartHandler, { passive: true });
    dialog.addEventListener('touchmove', swipeTouchMoveHandler, { passive: false });
    dialog.addEventListener('touchend', swipeTouchEndHandler, { passive: true });
  }
}

/**
 * @param {HTMLElement} root
 */
function hidePopup(root) {
  const toRestore = previousFocus;
  previousFocus = null;

  const dialog = root.querySelector('[role="dialog"]');
  if (dialog instanceof HTMLElement) {
    if (swipeTouchStartHandler) dialog.removeEventListener('touchstart', swipeTouchStartHandler);
    if (swipeTouchMoveHandler) dialog.removeEventListener('touchmove', swipeTouchMoveHandler);
    if (swipeTouchEndHandler) dialog.removeEventListener('touchend', swipeTouchEndHandler);
    swipeTouchStartHandler = null;
    swipeTouchMoveHandler = null;
    swipeTouchEndHandler = null;
  }

  root.classList.remove('is-open');
  root.setAttribute('aria-hidden', 'true');
  root.hidden = true;

  // Unlock background scroll
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  if (scrollLockTouchHandler) {
    document.removeEventListener('touchmove', scrollLockTouchHandler);
    scrollLockTouchHandler = null;
  }

  requestAnimationFrame(() => {
    toRestore?.focus();
  });
}

function init() {
  const root = document.getElementById('welcome-country-popup');
  if (!root) return;

  const shopify = window.Shopify ?? {};
  const currentCountry = shopify.country ?? '';
  const currentLanguage = shopify.language ?? '';
  const routesRoot = shopify.routes?.root ?? '/';
  const returnTo = window.location.pathname;

  if (!currentCountry) return;

  if (sessionStorage.getItem(SESSION_STORAGE_KEY) === '1') return;

  root.querySelectorAll('[data-welcome-popup-dismiss]').forEach((el) => {
    el.addEventListener('click', () => hidePopup(root));
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hidePopup(root);
  });

  root.querySelector('[data-welcome-popup-change-country]')?.addEventListener('click', () => {
    hidePopup(root);
    openFooterLocalizationDrawer();
  });

  const url =
    routesRoot.replace(/\/$/, '') +
    '/browsing_context_suggestions.json' +
    `?country[enabled]=true&country[exclude]=${encodeURIComponent(currentCountry)}` +
    `&language[enabled]=true&language[exclude]=${encodeURIComponent(currentLanguage)}`;

  fetch(url)
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data) return;

      const country = data.detected_values?.country ?? data.suggestions?.[0]?.parts?.country;
      if (!country?.handle) return;

      const detectedKey = country.handle.toUpperCase();

      // Skip if detected country matches current market
      if (country.handle === currentCountry) return;
      if (detectedKey === 'US') return;

      const languageCode = data.suggestions?.[0]?.parts?.language?.handle;

      sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
      showPopup(root, country);

      root.querySelector('[data-welcome-popup-shop-in]')?.addEventListener('click', () => {
        submitLocalizationForm(country.handle, languageCode, returnTo);
      });
    })
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
